import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EntityResponse } from '../../types/entity.types';

// Constants for magic numbers
const DAYS_IN_WEEK = 7;
const DAYS_IN_MONTH = 30;
const DAYS_IN_QUARTER = 90;

interface DateRangeFilterProps {
  value: { start: string; end: string } | null;
  onChange: (range: { start: string; end: string }) => void;
  isLoading?: boolean;
  className?: string;
  entityData?: EntityResponse | null;
}

interface DateErrors {
  start?: string;
  end?: string;
}

export function DateRangeFilter({
  value,
  onChange,
  isLoading = false,
  className,
  entityData
}: DateRangeFilterProps) {
  // Runtime validation: Enforce required date contract
  if (value && (!value.start || !value.end)) {
    console.error('[DateRangeFilter] Invalid props: Both start and end dates are required', value);
    // Component will handle this via local state initialization
  }

  // Error state for date validation
  const [errors, setErrors] = useState<DateErrors>({});

  // Local state for immediate visual feedback while typing
  const [localStartInput, setLocalStartInput] = useState<string>(value?.start || '');
  const [localEndInput, setLocalEndInput] = useState<string>(value?.end || '');
  const [isTyping, setIsTyping] = useState(false);

  // Sync local state when external value changes (e.g., preset button clicked)
  // Don't sync external changes while user is actively typing
  useEffect(() => {
    if (isTyping) return;

    setLocalStartInput(value?.start || '');
    setLocalEndInput(value?.end || '');
  }, [value?.start, value?.end, isTyping]);

  // Quick preset handlers (using UTC to avoid timezone issues)
  const setLastNDays = useCallback((days: number) => {
    const end = new Date();
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - days);

    setErrors({}); // Clear errors when using presets
    onChange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  }, [onChange]);

  // Validate financial year data
  const validateFinancialYearData = useCallback((endMonth: number, endDay: number): boolean => {
    if (endMonth < 1 || endMonth > 12) return false;
    if (endDay < 1 || endDay > 31) return false;

    // Special handling for February 29th
    if (endMonth === 2 && endDay === 29) {
      return true; // Allow Feb 29th but system will handle leap year adjustments
    }

    // Test with current year for validation
    const currentYear = new Date().getUTCFullYear();
    const testDate = new Date(Date.UTC(currentYear, endMonth - 1, endDay));
    return testDate.getUTCMonth() === endMonth - 1 && testDate.getUTCDate() === endDay;
  }, []);

  // Calculate financial year dates based on entity data
  const calculateFinancialYear = useCallback((isCurrentYear: boolean = true) => {
    if (!entityData?.financial_year_end_month || !entityData?.financial_year_end_day) {
      // Fallback to calendar year if financial year data is not available
      const year = new Date().getFullYear() - (isCurrentYear ? 0 : 1);
      return {
        start: `${year}-01-01`,
        end: `${year}-12-31`
      };
    }

    const endMonth = entityData.financial_year_end_month;
    const endDay = entityData.financial_year_end_day;

    // Validate financial year data
    if (!validateFinancialYearData(endMonth, endDay)) {
      const year = new Date().getFullYear() - (isCurrentYear ? 0 : 1);
      return {
        start: `${year}-01-01`,
        end: `${year}-12-31`
      };
    }

    try {
      // Use UTC methods to avoid timezone issues
      const today = new Date();
      const currentYear = today.getUTCFullYear();

      // Determine which financial year we're currently in (using UTC)
      const currentYearEnd = new Date(Date.UTC(currentYear, endMonth - 1, endDay));
      const isAfterYearEnd = today > currentYearEnd;

      let fyEndYear: number;
      if (isCurrentYear) {
        // Current FY: if we're after the FY end, we're in the next FY
        fyEndYear = isAfterYearEnd ? currentYear + 1 : currentYear;
      } else {
        // Previous FY: always one year before current FY end year
        const currentFyEndYear = isAfterYearEnd ? currentYear + 1 : currentYear;
        fyEndYear = currentFyEndYear - 1;
      }

      // Calculate FY end date using UTC
      const fyEnd = new Date(Date.UTC(fyEndYear, endMonth - 1, endDay));

      // Calculate FY start date (day after previous year's end) using UTC
      const fyStart = new Date(Date.UTC(fyEndYear - 1, endMonth - 1, endDay));
      fyStart.setUTCDate(fyStart.getUTCDate() + 1);

      return {
        start: fyStart.toISOString().split('T')[0],
        end: fyEnd.toISOString().split('T')[0]
      };
    } catch {
      // Return calendar year as fallback
      const year = new Date().getFullYear() - (isCurrentYear ? 0 : 1);
      return {
        start: `${year}-01-01`,
        end: `${year}-12-31`
      };
    }
  }, [entityData, validateFinancialYearData]);

  const setThisYear = useCallback(() => {
    const fy = calculateFinancialYear(true);
    setErrors({}); // Clear errors when using presets
    onChange(fy);
  }, [calculateFinancialYear, onChange]);

  const setLastYear = useCallback(() => {
    const fy = calculateFinancialYear(false);
    setErrors({}); // Clear errors when using presets
    onChange(fy);
  }, [calculateFinancialYear, onChange]);

  const setThisMonth = useCallback(() => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const lastDay = new Date(Date.UTC(year, now.getUTCMonth() + 1, 0)).getUTCDate();

    setErrors({}); // Clear errors when using presets
    onChange({
      start: `${year}-${month}-01`,
      end: `${year}-${month}-${lastDay}`
    });
  }, [onChange]);

  const setLastMonth = useCallback(() => {
    const now = new Date();
    const lastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const year = lastMonth.getUTCFullYear();
    const month = String(lastMonth.getUTCMonth() + 1).padStart(2, '0');
    const lastDay = new Date(Date.UTC(year, lastMonth.getUTCMonth() + 1, 0)).getUTCDate();

    setErrors({}); // Clear errors when using presets
    onChange({
      start: `${year}-${month}-01`,
      end: `${year}-${month}-${lastDay}`
    });
  }, [onChange]);

  // Format date for display (using UTC to avoid timezone issues)
  const formatDate = (dateStr: string) => {
    // Parse as UTC to avoid timezone interpretation issues
    const date = new Date(dateStr + 'T00:00:00.000Z');
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      timeZone: 'UTC' // Force UTC display to match our UTC calculations
    });
  };

  // Validate date string format
  const isValidDateString = useCallback((dateStr: string): boolean => {
    if (!dateStr) return false;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateStr)) return false;
    const date = new Date(dateStr + 'T00:00:00.000Z');
    return !isNaN(date.getTime());
  }, []);

  // Handle immediate local state updates (no store updates)
  const handleStartChange = useCallback((newStart: string) => {
    setLocalStartInput(newStart);
    setIsTyping(true);
    setErrors(prev => ({ ...prev, start: undefined }));
  }, []);

  const handleEndChange = useCallback((newEnd: string) => {
    setLocalEndInput(newEnd);
    setIsTyping(true);
    setErrors(prev => ({ ...prev, end: undefined }));
  }, []);

  // Handle blur events - validate and update store only when user finishes editing
  const handleStartBlur = useCallback(() => {
    setIsTyping(false);

    // CRITICAL: Validate date is not empty (required field)
    if (!localStartInput) {
      setErrors(prev => ({ ...prev, start: 'Start date is required' }));
      // Revert to previous valid value if available
      if (value?.start) {
        setLocalStartInput(value.start);
      }
      return;
    }

    // Validate date string format
    if (!isValidDateString(localStartInput)) {
      setErrors(prev => ({ ...prev, start: 'Please enter a valid date' }));
      // Revert to previous valid value
      if (value?.start) {
        setLocalStartInput(value.start);
      }
      return;
    }

    // Validate that start date isn't after end date
    if (localEndInput && localStartInput > localEndInput) {
      setErrors(prev => ({ ...prev, start: 'Start date cannot be after end date' }));
      // Revert to previous valid value
      if (value?.start) {
        setLocalStartInput(value.start);
      }
      return;
    }

    // Clear error and update store only if all validations pass
    setErrors(prev => ({ ...prev, start: undefined }));
    onChange({
      start: localStartInput,
      end: localEndInput || value?.end || ''
    });
  }, [localStartInput, localEndInput, value, onChange, isValidDateString]);

  const handleEndBlur = useCallback(() => {
    setIsTyping(false);

    // CRITICAL: Validate date is not empty (required field)
    if (!localEndInput) {
      setErrors(prev => ({ ...prev, end: 'End date is required' }));
      // Revert to previous valid value if available
      if (value?.end) {
        setLocalEndInput(value.end);
      }
      return;
    }

    // Validate date string format
    if (!isValidDateString(localEndInput)) {
      setErrors(prev => ({ ...prev, end: 'Please enter a valid date' }));
      // Revert to previous valid value
      if (value?.end) {
        setLocalEndInput(value.end);
      }
      return;
    }

    // Validate that end date isn't before start date
    if (localStartInput && localEndInput < localStartInput) {
      setErrors(prev => ({ ...prev, end: 'End date cannot be before start date' }));
      // Revert to previous valid value
      if (value?.end) {
        setLocalEndInput(value.end);
      }
      return;
    }

    // Clear error and update store only if all validations pass
    setErrors(prev => ({ ...prev, end: undefined }));
    onChange({
      start: localStartInput || value?.start || '',
      end: localEndInput
    });
  }, [localEndInput, localStartInput, value, onChange, isValidDateString]);

  // NOTE: Clear functionality removed - dates are now required
  // Users must always have a date range selected from presets or manual input

  // Preset detection logic - checks if current date range matches any preset
  // Memoized to prevent expensive recalculations on every render
  const activePreset = useMemo(() => {
    if (!value?.start || !value?.end) return null;

    // Helper to get date range for Last N Days preset
    const getLastNDaysRange = (days: number) => {
      const end = new Date();
      const start = new Date();
      start.setUTCDate(start.getUTCDate() - days);
      return {
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0]
      };
    };

    // Helper to get This Month range
    const getThisMonthRange = () => {
      const now = new Date();
      const year = now.getUTCFullYear();
      const month = String(now.getUTCMonth() + 1).padStart(2, '0');
      const lastDay = new Date(Date.UTC(year, now.getUTCMonth() + 1, 0)).getUTCDate();
      return {
        start: `${year}-${month}-01`,
        end: `${year}-${month}-${lastDay}`
      };
    };

    // Helper to get Last Month range
    const getLastMonthRange = () => {
      const now = new Date();
      const lastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      const year = lastMonth.getUTCFullYear();
      const month = String(lastMonth.getUTCMonth() + 1).padStart(2, '0');
      const lastDay = new Date(Date.UTC(year, lastMonth.getUTCMonth() + 1, 0)).getUTCDate();
      return {
        start: `${year}-${month}-01`,
        end: `${year}-${month}-${lastDay}`
      };
    };

    // Check each preset using named constants
    const last7Days = getLastNDaysRange(DAYS_IN_WEEK);
    if (value.start === last7Days.start && value.end === last7Days.end) return 'last7days';

    const last30Days = getLastNDaysRange(DAYS_IN_MONTH);
    if (value.start === last30Days.start && value.end === last30Days.end) return 'last30days';

    const last90Days = getLastNDaysRange(DAYS_IN_QUARTER);
    if (value.start === last90Days.start && value.end === last90Days.end) return 'last90days';

    const thisMonth = getThisMonthRange();
    if (value.start === thisMonth.start && value.end === thisMonth.end) return 'thismonth';

    const lastMonth = getLastMonthRange();
    if (value.start === lastMonth.start && value.end === lastMonth.end) return 'lastmonth';

    const thisFY = calculateFinancialYear(true);
    if (value.start === thisFY.start && value.end === thisFY.end) return 'thisfy';

    const lastFY = calculateFinancialYear(false);
    if (value.start === lastFY.start && value.end === lastFY.end) return 'lastfy';

    return null;
  }, [value, calculateFinancialYear]);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Aria-live region for dynamic error announcements */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {errors.start && `Start date error: ${errors.start}`}
        {errors.end && `End date error: ${errors.end}`}
      </div>

      {/* Quick Presets */}
      <div className="space-y-2">
        <Label className="text-xs text-gray-600">Quick Select:</Label>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setLastNDays(DAYS_IN_WEEK)}
            disabled={isLoading}
            aria-pressed={activePreset === 'last7days'}
            className={cn(
              "text-xs",
              activePreset === 'last7days' && "bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
            )}
          >
            Last 7 days
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setLastNDays(DAYS_IN_MONTH)}
            disabled={isLoading}
            aria-pressed={activePreset === 'last30days'}
            className={cn(
              "text-xs",
              activePreset === 'last30days' && "bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
            )}
          >
            Last 30 days
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setLastNDays(DAYS_IN_QUARTER)}
            disabled={isLoading}
            aria-pressed={activePreset === 'last90days'}
            className={cn(
              "text-xs",
              activePreset === 'last90days' && "bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
            )}
          >
            Last 90 days
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={setThisMonth}
            disabled={isLoading}
            aria-pressed={activePreset === 'thismonth'}
            className={cn(
              "text-xs",
              activePreset === 'thismonth' && "bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
            )}
          >
            This month
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={setLastMonth}
            disabled={isLoading}
            aria-pressed={activePreset === 'lastmonth'}
            className={cn(
              "text-xs",
              activePreset === 'lastmonth' && "bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
            )}
          >
            Last month
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={setThisYear}
            disabled={isLoading}
            aria-pressed={activePreset === 'thisfy'}
            className={cn(
              "text-xs",
              activePreset === 'thisfy' && "bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
            )}
          >
            This FY
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={setLastYear}
            disabled={isLoading}
            aria-pressed={activePreset === 'lastfy'}
            className={cn(
              "text-xs",
              activePreset === 'lastfy' && "bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
            )}
          >
            Last FY
          </Button>
        </div>
      </div>

      {/* Date Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="date-from" className="text-xs flex items-center justify-between">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              From Date
            </span>
            <span className="text-red-600 font-normal">(required)</span>
          </Label>
          <div className="relative">
            <Input
              id="date-from"
              type="date"
              value={localStartInput}
              onChange={(e) => handleStartChange(e.target.value)}
              onBlur={handleStartBlur}
              disabled={isLoading}
              required
              max={localEndInput || undefined}
              className={cn(
                errors.start && "border-red-500 focus-visible:ring-red-500"
              )}
              aria-invalid={!!errors.start}
              aria-describedby={errors.start ? "date-from-error" : undefined}
              aria-required="true"
            />
          </div>
          {errors.start && (
            <p id="date-from-error" className="text-xs text-red-600 mt-1" role="alert">
              {errors.start}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="date-to" className="text-xs flex items-center justify-between">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              To Date
            </span>
            <span className="text-red-600 font-normal">(required)</span>
          </Label>
          <div className="relative">
            <Input
              id="date-to"
              type="date"
              value={localEndInput}
              onChange={(e) => handleEndChange(e.target.value)}
              onBlur={handleEndBlur}
              disabled={isLoading}
              required
              className={cn(
                errors.end && "border-red-500 focus-visible:ring-red-500"
              )}
              min={localStartInput || undefined}
              aria-invalid={!!errors.end}
              aria-describedby={errors.end ? "date-to-error" : undefined}
              aria-required="true"
            />
          </div>
          {errors.end && (
            <p id="date-to-error" className="text-xs text-red-600 mt-1" role="alert">
              {errors.end}
            </p>
          )}
        </div>
      </div>

      {/* Active Filter Display */}
      {value && (value.start || value.end) && (
        <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
          <span className="text-sm text-blue-700 font-medium">
            <Calendar className="inline h-3 w-3 mr-1" />
            Showing: {
              value.start && value.end ?
                `${formatDate(value.start)} → ${formatDate(value.end)}` :
              value.start ?
                `From ${formatDate(value.start)} onwards` :
                `Up to ${formatDate(value.end)}`
            }
          </span>
        </div>
      )}
    </div>
  );
}